/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { FunctionComponent, VNode, Fragment } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { classes, VComponent } from '@adobe-commerce/elsie/lib';
import {
  Icon,
  Button,
  Skeleton,
  SkeletonRow,
} from '@adobe-commerce/elsie/components';
import { useText } from '@adobe-commerce/elsie/i18n';

import '@adobe-commerce/elsie/components/Table/Table.css';

type Sortable = 'asc' | 'desc' | true;

type Column =
  | { label: string; key: string; ariaLabel?: string; sortBy?: Sortable }
  | {
      label: VNode<HTMLAttributes<HTMLElement>>;
      key: string;
      ariaLabel: string;
      sortBy?: Sortable;
    };

type RowData = {
  [key: string]: VNode | string | number | undefined;
  _rowDetails?: VNode | string; // Special property for expandable row content
};

export interface TableProps
  extends Omit<HTMLAttributes<HTMLTableElement>, 'loading'> {
  columns: Column[];
  rowData: RowData[];
  mobileLayout?: 'stacked' | 'none';
  caption?: string;
  expandedRows?: Set<number>;
  loading?: boolean;
  skeletonRowCount?: number;
  onSortChange?: (columnKey: string, direction: Sortable) => void;
}

export const Table: FunctionComponent<TableProps> = ({
  className,
  children,
  columns = [],
  rowData = [],
  mobileLayout = 'none',
  caption,
  expandedRows = new Set(),
  loading = false,
  skeletonRowCount = 10,
  onSortChange,
  ...props
}) => {
  const translations = useText({
    sortedAscending: 'Dropin.Table.sortedAscending',
    sortedDescending: 'Dropin.Table.sortedDescending',
    sortBy: 'Dropin.Table.sortBy',
  });

  const handleSort = (column: Column) => {
    if (!onSortChange) return;

    // Determine next sort direction
    let nextDirection: Sortable;
    if (column.sortBy === true) {
      nextDirection = 'asc';
    } else if (column.sortBy === 'asc') {
      nextDirection = 'desc';
    } else {
      nextDirection = true;
    }

    onSortChange(column.key, nextDirection);
  };

  const renderSortButton = (column: Column) => {
    if (column.sortBy === undefined) return null;
    const label = column.ariaLabel ?? (column.label as string);

    let iconSource: string;
    let ariaLabel: string;

    if (column.sortBy === 'asc') {
      iconSource = 'ChevronUp';
      ariaLabel = translations.sortedAscending.replace('{label}', label);
    } else if (column.sortBy === 'desc') {
      iconSource = 'ChevronDown';
      ariaLabel = translations.sortedDescending.replace('{label}', label);
    } else {
      // Show chevron down when sortable but not sorted
      iconSource = 'ChevronDown';
      ariaLabel = translations.sortBy.replace('{label}', label);
    }

    return (
      <Button
        variant="tertiary"
        size="medium"
        className="dropin-table__header__sort-button"
        icon={<Icon source={iconSource} />}
        aria-label={ariaLabel}
        onClick={() => handleSort(column)}
      />
    );
  };

  const renderSkeletonRows = () => {
    return Array.from({ length: skeletonRowCount }, (_, rowIndex) => (
      <tr key={`skeleton-${rowIndex}`} className="dropin-table__body__row">
        {columns.map((column) => (
          <td
            key={column.key}
            className="dropin-table__body__cell"
            data-label={column.ariaLabel ?? column.label}
          >
            <Skeleton>
              <SkeletonRow variant="row" size="small" fullWidth />
            </Skeleton>
          </td>
        ))}
      </tr>
    ));
  };

  const renderDataRows = () => {
    return rowData.map((row, rowIndex) => {
      const hasDetails = row._rowDetails !== undefined;
      const isExpanded = expandedRows.has(rowIndex);

      return (
        <Fragment key={rowIndex}>
          <tr className={classes([
            'dropin-table__body__row',
            ['dropin-table__body__row--expanded', isExpanded && hasDetails],
          ])}>
            {columns.map((column) => {
              const cell = row[column.key];
              const label = column.ariaLabel ?? column.label;

              if (typeof cell === 'string' || typeof cell === 'number') {
                return (
                  <td
                    key={column.key}
                    className="dropin-table__body__cell"
                    data-label={label}
                  >
                    {cell}
                  </td>
                );
              }

              return (
                <td
                  key={column.key}
                  className="dropin-table__body__cell"
                  data-label={label}
                >
                  <VComponent node={cell!} />
                </td>
              );
            })}
          </tr>
          {hasDetails && isExpanded && (
            <tr
              key={`${rowIndex}-details`}
              className="dropin-table__row-details dropin-table__row-details--expanded"
              id={`row-${rowIndex}-details`}
            >
              <td
                className="dropin-table__row-details__cell"
                colSpan={columns.length}
                role="region"
                aria-labelledby={`row-${rowIndex}-details`}
              >
                {typeof row._rowDetails === 'string' ? (
                  row._rowDetails
                ) : (
                  <VComponent node={row._rowDetails!} />
                )}
              </td>
            </tr>
          )}
        </Fragment>
      );
    });
  };

  const getAriaSort = (
    column: Column
  ): 'none' | 'ascending' | 'descending' | 'other' | undefined => {
    if (column.sortBy === true) return 'none';
    if (column.sortBy === 'asc') return 'ascending';
    if (column.sortBy === 'desc') return 'descending';
    return undefined;
  };

  return (
    <div
      className={classes([
        'dropin-table',
        `dropin-table--mobile-layout-${mobileLayout}`,
        className,
      ])}
    >
      <table {...props} className="dropin-table__table">
        {caption && (
          <caption className="dropin-table__caption">{caption}</caption>
        )}
        <thead className="dropin-table__header">
          <tr className="dropin-table__header__row">
            {columns.map((column) => (
              <th
                key={column.key}
                className={classes([
                  'dropin-table__header__cell',
                  [
                    'dropin-table__header__cell--sorted',
                    column.sortBy === 'asc' || column.sortBy === 'desc',
                  ],
                  [
                    'dropin-table__header__cell--sortable',
                    column.sortBy !== undefined,
                  ],
                ])}
                aria-sort={getAriaSort(column)}
              >
                {column.label}
                {renderSortButton(column)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="dropin-table__body">
          {loading
            ? // Render skeleton rows when loading
              renderSkeletonRows()
            : // Render actual data when not loading
              renderDataRows()}
        </tbody>
      </table>
    </div>
  );
};
